import React, { useState, useEffect } from 'react';
import { FileText, RefreshCw, CheckCircle, Clock, XCircle, Eye, Plus, Edit2, Search, Filter, Building2, Download, Send, X, BarChart3 } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { organizationsService } from 'api/services/organizations.service';
import { WhatsAppTemplate, PaginatedResponse, TemplateInsights } from 'types';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';
import TemplateCreateModal from 'components/WhatsApp/TemplateCreateModal';
import { useAuth } from 'hooks/useAuth';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const WhatsAppTemplates: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [data, setData] = useState<PaginatedResponse<WhatsAppTemplate> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingFromMeta, setSyncingFromMeta] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<WhatsAppTemplate | null>(null);

  // Send Test Template state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testTemplate, setTestTemplate] = useState<WhatsAppTemplate | null>(null);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testParameters, setTestParameters] = useState<Record<string, string>>({});
  const [sendingTest, setSendingTest] = useState(false);

  // Template Insights state
  const [insightsTemplate, setInsightsTemplate] = useState<WhatsAppTemplate | null>(null);
  const [insightsData, setInsightsData] = useState<TemplateInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  // Organization selector
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  useEffect(() => {
    if (!needsOrgSelection) {
      fetchTemplates();
    }
  }, [currentPage, searchQuery, statusFilter, categoryFilter, languageFilter, selectedOrgId, needsOrgSelection]);

  const fetchTemplates = async () => {
    if (needsOrgSelection) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const orgId = getOrganizationId();
    const result = await whatsappService.getTemplates({
      page: currentPage,
      size: ITEMS_PER_PAGE,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      organizationId: orgId,
    });

    if (result.success && result.data) {
      // Apply client-side filters
      let filteredData = result.data.data;

      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        filteredData = filteredData.filter(t =>
          t.name.toLowerCase().includes(searchLower)
        );
      }

      if (categoryFilter !== 'all') {
        filteredData = filteredData.filter(t => t.category === categoryFilter);
      }

      if (languageFilter !== 'all') {
        filteredData = filteredData.filter(t => t.language === languageFilter);
      }

      setData({
        ...result.data,
        data: filteredData,
        total: filteredData.length,
      });
    } else {
      setError(result.error || 'Failed to fetch templates');
    }

    setLoading(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const handleRefreshTemplates = async () => {
    setSyncing(true);
    await fetchTemplates();
    setSyncing(false);
  };

  const handleSyncFromMeta = async () => {
    const orgId = getOrganizationId();
    if (!orgId) {
      alert('Please select an organization to sync templates');
      return;
    }

    setSyncingFromMeta(true);
    const result = await whatsappService.syncTemplates(orgId);

    if (result.success) {
      const s: any = result.data || {};
      const summary = (s.added !== undefined)
        ? `Synced from Meta — ${s.added ?? 0} added, ${s.updated ?? 0} updated, ${s.removed ?? 0} removed.`
        : 'Templates synced successfully from Meta!';
      alert(summary);
      await fetchTemplates();
    } else {
      alert(`Failed to sync templates: ${result.error}`);
    }

    setSyncingFromMeta(false);
  };

  const handleCreateTemplate = (template: WhatsAppTemplate) => {
    alert('Template submitted for approval! It will be reviewed by WhatsApp within 1-2 hours.');
    setShowCreateModal(false);
    setEditTemplate(null);
    fetchTemplates();
  };

  const handleEditTemplate = (template: WhatsAppTemplate) => {
    setEditTemplate(template);
    setShowCreateModal(true);
  };

  const openTestModal = (template: WhatsAppTemplate) => {
    setTestTemplate(template);
    setTestPhoneNumber('');
    // Initialize parameters based on parameterCount
    const params: Record<string, string> = {};
    for (let i = 1; i <= (template.parameterCount || 0); i++) {
      params[String(i)] = '';
    }
    // Media-header templates must supply the header media at send time, else Meta
    // rejects with #132012 (header format mismatch). Pre-fill from the template's
    // sample so the send includes a valid header; the user can override the URL.
    const media = getHeaderMedia(template);
    if (media) {
      const key = media.format === 'VIDEO' ? 'header_video'
        : media.format === 'DOCUMENT' ? 'header_document' : 'header_image';
      params[key] = media.url;
    }
    // Dynamic URL buttons (url contains a {{n}} placeholder) need their suffix
    // supplied at send time, else Meta rejects with #131008. Static buttons don't.
    getTemplateButtons(template).forEach((b, idx) => {
      if (b.type === 'URL' && b.url && b.url.includes('{{')) {
        params[`button_url_${idx}`] = '';
      }
    });
    setTestParameters(params);
    setShowTestModal(true);
  };

  const handleSendTestTemplate = async () => {
    if (!testTemplate || !testPhoneNumber.trim()) return;

    setSendingTest(true);

    const result = await whatsappService.sendTemplateMessage({
      to: testPhoneNumber,
      templateName: testTemplate.name,
      templateLanguage: testTemplate.language,
      templateParameters: Object.keys(testParameters).length > 0 ? testParameters : undefined,
    });

    if (result.success) {
      alert('Test template message sent successfully!');
      setShowTestModal(false);
      setTestTemplate(null);
      setTestPhoneNumber('');
      setTestParameters({});
    } else {
      alert(`Failed to send test message: ${result.error}`);
    }

    setSendingTest(false);
  };

  const openInsightsModal = async (template: WhatsAppTemplate) => {
    if (!template.id) return;
    setInsightsTemplate(template);
    setInsightsData(null);
    setInsightsError(null);
    setInsightsLoading(true);
    const result = await whatsappService.getTemplateInsights(template.id, 30);
    if (result.success && result.data) {
      setInsightsData(result.data);
    } else {
      setInsightsError(result.error || 'Failed to load insights');
    }
    setInsightsLoading(false);
  };

  const closeInsightsModal = () => {
    setInsightsTemplate(null);
    setInsightsData(null);
    setInsightsError(null);
    setInsightsLoading(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 rounded text-xs font-medium flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'PENDING':
        return (
          <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400 rounded text-xs font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'REJECTED':
        return (
          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400 rounded text-xs font-medium flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'REMOVED':
        return (
          <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs font-medium flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Removed from Meta
          </span>
        );
      default:
        return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs">{status}</span>;
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      MARKETING: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400',
      UTILITY: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400',
      AUTHENTICATION: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-400',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[category] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'}`}>
        {category}
      </span>
    );
  };

  // Extract a media-header URL (IMAGE/VIDEO/DOCUMENT) from the template's Meta
  // components, which Meta provides as a sample under example.header_handle.
  const getHeaderMedia = (template: WhatsAppTemplate): { format: string; url: string } | null => {
    if (!template.templateStructure) return null;
    try {
      const components = JSON.parse(template.templateStructure);
      if (!Array.isArray(components)) return null;
      const header = components.find((c: any) => c?.type === 'HEADER');
      if (!header) return null;
      const format = (header.format || '').toUpperCase();
      if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) return null;
      const url = header.example?.header_handle?.[0];
      return url ? { format, url } : null;
    } catch {
      return null;
    }
  };

  // Extract buttons from the template's Meta components or the buttons JSON field.
  const getTemplateButtons = (template: WhatsAppTemplate): Array<{ type: string; text: string; url?: string }> => {
    try {
      if (template.templateStructure) {
        const components = JSON.parse(template.templateStructure);
        const comp = Array.isArray(components) && components.find((c: any) => c?.type === 'BUTTONS');
        if (comp?.buttons) return comp.buttons;
      }
      if (template.buttons) {
        const parsed = JSON.parse(template.buttons);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore malformed
    }
    return [];
  };

  const renderTemplatePreview = (template: WhatsAppTemplate) => {
    const headerMedia = getHeaderMedia(template);
    const buttons = getTemplateButtons(template);
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-w-md">
        {headerMedia && (
          <div className="mb-3">
            {headerMedia.format === 'IMAGE' && (
              <img src={headerMedia.url} alt="Template header" className="max-w-full rounded" />
            )}
            {headerMedia.format === 'VIDEO' && (
              <video controls className="max-w-full rounded"><source src={headerMedia.url} /></video>
            )}
            {headerMedia.format === 'DOCUMENT' && (
              <a href={headerMedia.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">
                View document header
              </a>
            )}
          </div>
        )}
        {template.headerText && (
          <div className="mb-3">
            <h3 className="font-bold text-gray-900 dark:text-white">{template.headerText}</h3>
          </div>
        )}

        {template.bodyText && (
          <div className="mb-3">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{template.bodyText}</p>
          </div>
        )}

        {template.footerText && (
          <div className="text-xs text-gray-500 dark:text-gray-400 italic">
            {template.footerText}
          </div>
        )}

        {buttons.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
            {buttons.map((b, i) => (
              <div key={i} className="text-center text-sm text-blue-600 dark:text-blue-400 py-1.5 rounded bg-gray-50 dark:bg-gray-700/50">
                {b.type === 'URL' ? '🔗 ' : b.type === 'PHONE_NUMBER' ? '📞 ' : '↩️ '}{b.text}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error && !data) return <ErrorMessage message={error} onRetry={fetchTemplates} />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FileText className="w-7 h-7" />
            WhatsApp Templates
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total: {data.total} templates
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSyncFromMeta}
            disabled={syncingFromMeta || needsOrgSelection}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title={needsOrgSelection ? 'Select an organization first' : 'Sync templates from Meta'}
          >
            {syncingFromMeta ? (
              <>
                <Download className="w-5 h-5 animate-pulse" />
                Syncing...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Sync from Meta
              </>
            )}
          </button>
          <button
            onClick={handleRefreshTemplates}
            disabled={syncing}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Refresh
              </>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Template
          </button>
        </div>
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Please select an organization from the dropdown above to view WhatsApp templates.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {!needsOrgSelection && <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search templates by name..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter Options */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
              <option value="AUTHENTICATION">Authentication</option>
            </select>
          </div>

          {/* Language Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Language:</label>
            <select
              value={languageFilter}
              onChange={(e) => { setLanguageFilter(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="hi">Hindi</option>
              <option value="ar">Arabic</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all' || languageFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setCategoryFilter('all');
                setLanguageFilter('all');
                handleFilterChange();
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>}

      {/* Templates List */}
      {!needsOrgSelection && <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Template Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Language
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900 dark:text-white">{template.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 dark:text-white">{template.language.toUpperCase()}</span>
                  </td>
                  <td className="px-6 py-4">
                    {getCategoryBadge(template.category)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(template.status)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedTemplate(template)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openInsightsModal(template)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition"
                        title="Insights"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      {template.status === 'APPROVED' && (
                        <button
                          onClick={() => openTestModal(template)}
                          className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition"
                          title="Send Test"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>}

      {/* Preview Modal */}
      {selectedTemplate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTemplate(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedTemplate.name}</h2>
                <div className="flex gap-2 mt-2">
                  {getStatusBadge(selectedTemplate.status)}
                  {getCategoryBadge(selectedTemplate.category)}
                </div>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <div className="border-t dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</h3>
              {renderTemplatePreview(selectedTemplate)}
            </div>

            <div className="border-t dark:border-gray-700 mt-6 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Template Details</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Language</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTemplate.language.toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Parameters</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTemplate.parameterCount || 0}</p>
                  </div>
                </div>
                {selectedTemplate.metaTemplateId && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Meta Template ID</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{selectedTemplate.metaTemplateId}</p>
                  </div>
                )}
                {selectedTemplate.approvedAt && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Approved At</p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTemplate.approvedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setSelectedTemplate(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Template Modal */}
      {showTestModal && testTemplate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTestModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-purple-600" />
                  Send Test Template
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Template: <span className="font-medium">{testTemplate.name}</span>
                </p>
              </div>
              <button
                onClick={() => setShowTestModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Template Preview */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template Preview</h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {testTemplate.headerText && (
                    <p className="font-semibold mb-1">{testTemplate.headerText}</p>
                  )}
                  {testTemplate.bodyText && (
                    <p className="whitespace-pre-line">{testTemplate.bodyText}</p>
                  )}
                  {testTemplate.footerText && (
                    <p className="text-xs italic mt-2">{testTemplate.footerText}</p>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  {getStatusBadge(testTemplate.status)}
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-300">
                    {testTemplate.language.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Recipient Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={testPhoneNumber}
                  onChange={(e) => setTestPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter phone number in E.164 format (e.g., +1234567890)
                </p>
              </div>

              {/* Header media (image/video/document) — required at send time */}
              {(testParameters.header_image !== undefined || testParameters.header_video !== undefined || testParameters.header_document !== undefined) && (() => {
                const mediaKey = testParameters.header_image !== undefined ? 'header_image'
                  : testParameters.header_video !== undefined ? 'header_video' : 'header_document';
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Header {mediaKey.replace('header_', '')} URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={testParameters[mediaKey]}
                      onChange={(e) => setTestParameters({ ...testParameters, [mediaKey]: e.target.value })}
                      placeholder="https://.../image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Publicly-reachable media URL for the template header (pre-filled from the template sample).
                    </p>
                  </div>
                );
              })()}

              {/* Dynamic URL button parameters */}
              {Object.keys(testParameters).filter(k => k.startsWith('button_url_')).map((key) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Button URL suffix (button {Number(key.replace('button_url_', '')) + 1}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={testParameters[key]}
                    onChange={(e) => setTestParameters({ ...testParameters, [key]: e.target.value })}
                    placeholder="dynamic part of the button URL"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                </div>
              ))}

              {/* Template Parameters (numeric body placeholders only) */}
              {(testTemplate.parameterCount || 0) > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Parameters
                  </label>
                  <div className="space-y-2">
                    {Object.keys(testParameters).filter(k => /^\d+$/.test(k)).map((key) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-16">
                          {`{{${key}}}`}
                        </span>
                        <input
                          type="text"
                          value={testParameters[key]}
                          onChange={(e) => setTestParameters({
                            ...testParameters,
                            [key]: e.target.value
                          })}
                          placeholder={`Parameter ${key}`}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Fill in the parameter values to replace placeholders in the template
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendTestTemplate}
                  disabled={!testPhoneNumber.trim() || sendingTest}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Test
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Insights Modal */}
      {insightsTemplate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeInsightsModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Template Insights
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {insightsTemplate.name} · last 30 days
                </p>
              </div>
              <button
                onClick={closeInsightsModal}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            {insightsLoading ? (
              <div className="py-12 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : insightsError ? (
              <div className="py-8">
                <ErrorMessage message={insightsError} />
              </div>
            ) : insightsData ? (
              <div className="space-y-6">
                {/* Funnel */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Funnel</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Sent</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{insightsData.funnel.sent}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium">Delivered</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100">{insightsData.funnel.delivered}</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Read</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{insightsData.funnel.read}</p>
                    </div>
                  </div>
                </div>

                {/* Button Clicks */}
                <div className="border-t dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Button Clicks</h3>
                  {insightsData.buttons.length === 0 ? (
                    <p className="text-sm text-gray-400">No button clicks yet</p>
                  ) : (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2">Label</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2 text-right">Clicks</th>
                            <th className="px-3 py-2 text-right">Click rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {insightsData.buttons.map((b, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{b.label}</td>
                              <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">{b.type}</td>
                              <td className="px-3 py-1.5 text-right font-medium text-gray-900 dark:text-white">{b.clicks}</td>
                              <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-300">{b.clickRate}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Performance Trend */}
                <div className="border-t dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Performance Trend</h3>
                  {insightsData.trend.length === 0 ? (
                    <p className="text-sm text-gray-400">No trend data</p>
                  ) : (
                    <div className="space-y-1">
                      {insightsData.trend.map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5">
                          <span className="text-gray-700 dark:text-gray-300">{new Date(t.start * 1000).toLocaleDateString()}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="text-blue-600 dark:text-blue-400">{t.sent} sent</span>
                            {' · '}
                            <span className="text-green-600 dark:text-green-400">{t.delivered} delivered</span>
                            {' · '}
                            <span className="text-purple-600 dark:text-purple-400">{t.read} read</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Error Analysis */}
                <div className="border-t dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Error Analysis</h3>
                  {insightsData.errorAnalysis.length === 0 ? (
                    <p className="text-sm text-gray-400">No failures</p>
                  ) : (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2">Error code</th>
                            <th className="px-3 py-2 text-right">Count</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {insightsData.errorAnalysis.map((e, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 font-mono text-xs text-red-600 dark:text-red-400">{e.errorCode}</td>
                              <td className="px-3 py-1.5 text-right font-medium text-gray-900 dark:text-white">{e.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
              <button
                onClick={closeInsightsModal}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Create/Edit Modal */}
      <TemplateCreateModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditTemplate(null);
        }}
        onSuccess={handleCreateTemplate}
        editTemplate={editTemplate}
      />
    </div>
  );
};

export default WhatsAppTemplates;
